/**
 * Helpers for honoring HTTP Retry-After from the backend.
 *
 * When gorilla's circuit breakers shed load they return Retry-After either on
 * a real HTTP 503 (total shed) or on HTTP 200 with incomplete runs (partial
 * shed). Retrying sooner is pointless and keeps the breaker open, so Apollo
 * (503) and the incomplete-refetch ladder (200) both wait out the hint.
 */

/** Cap on a server-provided Retry-After so a bad header can't stall panels. */
export const MAX_RETRY_AFTER_MS = 60_000;

/** GraphQL extensions key set by retryAfterExtensionsLink from the header. */
export const RETRY_AFTER_SECONDS_EXTENSION = 'retryAfterSeconds';

/**
 * Apollo operation-context key for a mutable holder that
 * retryAfterExtensionsLink fills when Retry-After is present.
 *
 * Needed because Apollo Client 3's `client.query()` returns ApolloQueryResult,
 * which has no `extensions` field — so writing onto FetchResult.extensions is
 * dropped before incomplete-refetch can read it.
 */
export const RETRY_AFTER_HOLDER_CONTEXT_KEY = 'retryAfterSecondsHolder';

/** Mutable bag passed via operation context; filled by retryAfterExtensionsLink. */
export type RetryAfterSecondsHolder = {seconds?: number};

/**
 * Total tries (initial + retries) for a 503. Deliberately lower than the
 * 6 used for other 5xx: a 503 means the server is shedding load on purpose.
 */
export const MAX_ATTEMPTS_503 = 3;

/**
 * The error here is the ServerError thrown by apollo-link-http on non-2xx
 * responses: {statusCode, response} where response is the fetch Response.
 */
export function is503Error(error: any): boolean {
  return error != null && error.statusCode === 503;
}

/**
 * Caps a non-negative integer Retry-After (seconds) to ms.
 * Returns null when the value is not a usable non-negative finite number.
 */
export function retryAfterSecondsToMs(seconds: number): number | null {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  return Math.min(Math.floor(seconds) * 1000, MAX_RETRY_AFTER_MS);
}

/**
 * Parses a Retry-After header value (integer seconds) into capped ms.
 * Returns null when missing/unreadable or not a non-negative integer
 * (the HTTP-date form is not emitted by our backend).
 */
export function parseRetryAfterHeaderMs(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  return retryAfterSecondsToMs(parseInt(trimmed, 10));
}

/**
 * Reads the Retry-After header off a ServerError, in ms.
 */
export function getRetryAfterMs(error: any): number | null {
  const headers = error?.response?.headers;
  if (headers == null || typeof headers.get !== 'function') {
    return null;
  }
  return parseRetryAfterHeaderMs(headers.get('retry-after'));
}

/**
 * Delay before an incomplete-refetch when the prior 200 response carried
 * Retry-After (partial shed). Includes up to 1s jitter. Returns 0 when the
 * server gave no usable hint so the caller can proceed immediately.
 */
export function getIncompleteRetryDelayMs(
  retryAfterSeconds: number | undefined,
  random: () => number = Math.random
): number {
  if (retryAfterSeconds == null) {
    return 0;
  }
  const ms = retryAfterSecondsToMs(retryAfterSeconds);
  if (ms == null) {
    return 0;
  }
  return ms + random() * 1000;
}

/**
 * Delay before re-attempting a 503'd operation: the server-directed
 * Retry-After plus up to 1s of jitter (so panels don't stampede the
 * half-open breaker at the same instant), or a jittered 5-15s when the
 * response carried no usable header.
 */
export function get503RetryDelayMs(
  error: any,
  random: () => number = Math.random
): number {
  const retryAfterMs = getRetryAfterMs(error);
  if (retryAfterMs != null) {
    return retryAfterMs + random() * 1000;
  }
  return 5000 + random() * 10_000;
}
